import { createSlice } from '@reduxjs/toolkit';
import axios from 'src/utils/axios';
import parseQueryParams from 'src/utils/query';

const initialState = {
  results: null,
  count: 0,
  capped: false,
  loading: false,
  error: null,
};

const slice = createSlice({
  name: 'HouseholdSearch',
  initialState,
  reducers: {
    startLoading(state) {
      state.loading = true;
      state.error = null;
    },
    getSearchResultsSuccess(state, action) {
      state.loading = false;
      state.error = null;
      state.results = action.payload.results ?? [];
      state.count = action.payload.count ?? 0;
      state.capped = action.payload.capped ?? false;
    },
    clearSearchResults(state) {
      state.results = null;
      state.count = 0;
      state.capped = false;
      state.error = null;
    },
    hasError(state, action) {
      state.loading = false;
      state.error = action.payload;
    },
  },
});

export default slice.reducer;

export const { clearSearchResults } = slice.actions;

const EXPAND = [
  'member_type',
  'household.unit.building',
  'household.unit.building.applicant_pool',
  'household.unit.building.property',
  'household.head_of_household',
  'household.certifications.property',
  'household.certifications.category',
  'household.certifications.status',
  'active_certification.category',
].join(',');

const FIELDS = [
  'id',
  'active_certification',
  'content_type_id',
  'email',
  'first_name',
  'last_name',
  'slug',
  'middle_name',
  'matched_fields',
  'member_type.id',
  'member_type.name',
  'preferred_language',
  'household.id',
  'household.name',
  'household.slug',
  'household.head_of_household.id',
  'household.head_of_household.first_name',
  'household.head_of_household.middle_name',
  'household.head_of_household.last_name',
  'household.unit.id',
  'household.unit.active_certification',
  'household.unit.name',
  'household.unit.building.id',
  'household.unit.building.name',
  'household.unit.building.applicant_pool.id',
  'household.unit.building.applicant_pool.name',
  'household.unit.building.property.id',
  'household.unit.building.property.name',
  'household.unit.building.property.slug',
  'household.certifications.id',
  'household.certifications.log_number',
  'household.certifications.display_name',
  'household.certifications.is_active',
  'household.certifications.is_done',
  'household.certifications.property',
  'household.certifications.created',
  'household.certifications.category.id',
  'household.certifications.category.name',
  'household.certifications.status.id',
  'household.certifications.status.is_terminal',
  'household.certifications.property.id',
  'household.certifications.property.slug',
  'household.certifications.property.name',
  'active_certification.category.id',
  'active_certification.category.name',
].join(',');

export function fetchHouseholdSearchResults({
  search,
  propertyId,
  includeTerminalStatusCerts,
  page = 1,
  pageSize = 'all',
}) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const queryParams = {
        ...(search ? { search } : {}),
        property_id: propertyId ?? 0,
        include_terminal_status_certs: Boolean(includeTerminalStatusCerts),
        expand: EXPAND,
        fields: FIELDS,
      };
      if (pageSize === 'all') {
        queryParams.page_size = 'all';
      } else {
        queryParams.page = page;
        queryParams.page_size = pageSize;
      }
      const response = await axios.get(
        `household/household_member/search_members/?${parseQueryParams(
          queryParams
        )}`
      );
      const data = response.data;
      const results = Array.isArray(data) ? data : data.results ?? [];
      const count =
        typeof data.count === 'number' ? data.count : results.length;
      const capped = Boolean(data.capped);
      dispatch(
        slice.actions.getSearchResultsSuccess({
          results,
          count,
          capped,
        })
      );
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function clearHouseholdSearchResults() {
  return (dispatch) => {
    dispatch(slice.actions.clearSearchResults());
  };
}
